'use client';

import { lazy, Suspense, useMemo } from 'react';
import { useReducedMotion } from '@mantine/hooks';
import { DARK_THEME_MODES } from '@constants/colors';
import { useTheme } from '@contexts/ThemeContext';
import './login-background.css';

/**
 * LoginBackground: the full-viewport backdrop of the login page.
 *
 * React Bits' Scanner (see ../scanner/Scanner.tsx) is a travelling scan sweep
 * over a rippling signal field. It is an overlay shader — it paints lines and
 * leaves the troughs transparent — so this component owns the base color under
 * it, the scrim that keeps the form legible on top of it, and the two palettes.
 *
 * Colors are the brand tokens, not the vendor's demo purple: the ink and accent
 * here are the same values login-showcase.css uses (--ls-ink, --ls-ink-3,
 * --ls-accent), so the glass showcase panel sits on a continuous surface.
 *
 * Motion is motivated: JARVIS reads customs documents, and a slow scan pass is
 * that act. It runs well below the demo's defaults (speed 0.32 vs 0.5, sweep
 * 0.16 vs 0.25) because a full viewport travels far more distance per unit of
 * speed than the docs' 600px box, and because the right-hand feature stage
 * already carries the page's motion budget.
 *
 * prefers-reduced-motion and machines without WebGL2 both fall back to a static
 * banded gradient in the same palette; nothing on the page depends on the canvas.
 */

// WebGL2 only — the shader is `#version 300 es`. Probed once per page load.
let webgl2Supported: boolean | null = null;
const supportsWebgl2 = (): boolean => {
  if (webgl2Supported !== null) return webgl2Supported;
  if (typeof document === 'undefined') return false;
  try {
    webgl2Supported = !!document.createElement('canvas').getContext('webgl2');
  } catch {
    webgl2Supported = false;
  }
  return webgl2Supported;
};

// The shader is heavy and desktop-first; keep ogl out of the login bundle's critical path.
const Scanner = lazy(() => import('@components/scanner/Scanner'));

type Palette = {
  /** Painted under the shader — the shader only supplies the lines. */
  base: string;
  /** Trough / low-intensity color. */
  color1: string;
  /** Mid-ramp color. */
  color2: string;
  /** Sweep-crest color. */
  color3: string;
  glow: number;
  brightness: number;
  opacity: number;
  /** Scrim tint pooled behind the form column so labels stay readable. */
  scrim: string;
  /** Static band color for the reduced-motion / no-WebGL2 fallback. */
  fallbackBand: string;
};

/**
 * Dark themes: light lines rising to near-white on the sweep crest, over brand
 * ink. Light themes: the ramp is inverted, because an additive line shader
 * washes out on a pale base — here the crest is the darkest value, so the scan
 * reads as ink drawn onto slate rather than light thrown onto it.
 */
const DARK_PALETTE: Palette = {
  base: '#0b1c2b', // --ls-ink
  color1: '#16304a', // --ls-ink-3
  color2: '#2563eb', // --ls-accent
  color3: '#f8fafc', // slate 50
  glow: 0.22,
  brightness: 0.8,
  opacity: 0.8,
  scrim: 'rgba(11, 28, 43, 0.72)',
  fallbackBand: 'rgba(37, 99, 235, 0.16)',
};

const LIGHT_PALETTE: Palette = {
  base: '#eef2f6',
  color1: '#94a3b8', // slate 400
  color2: '#2563eb', // --ls-accent
  color3: '#0f2a44', // brand ink, lifted
  glow: 0.12,
  // The pale base swallows more of the ramp than ink does, so the light
  // variant needs a hotter line at a lower overall opacity to read at all.
  brightness: 0.95,
  opacity: 0.55,
  scrim: 'rgba(238, 242, 246, 0.74)',
  fallbackBand: 'rgba(15, 42, 68, 0.07)',
};

export default function LoginBackground() {
  const { mode } = useTheme();
  const reduce = useReducedMotion();

  const isDark = DARK_THEME_MODES.includes(mode);
  const palette = isDark ? DARK_PALETTE : LIGHT_PALETTE;
  // Under reduced motion the shader would still burn a rAF per frame to redraw
  // an identical image, so drop the canvas entirely rather than freezing it.
  const canAnimate = !reduce && supportsWebgl2();

  const style = useMemo(
    () =>
      ({
        '--lb-base': palette.base,
        '--lb-scrim': palette.scrim,
        '--lb-band': palette.fallbackBand,
      }) as React.CSSProperties,
    [palette]
  );

  return (
    <div className="lb-root" style={style} aria-hidden="true">
      {canAnimate ? (
        <Suspense fallback={null}>
          <div className="lb-canvas">
            <Scanner
              color1={palette.color1}
              color2={palette.color2}
              color3={palette.color3}
              speed={0.32}
              sweepSpeed={0.16}
              // Narrower than the docs' 1.6 so roughly three quarters of a sweep
              // period fits the viewport: one clear bright pass, one clear rest.
              sweepWidth={1.4}
              sweepFalloff={6}
              // Wider than the docs' 1.5: at viewport size the default packs the
              // bands tight enough to shimmer against the form.
              scale={2}
              frequency={2}
              ripple={0.22}
              bandDensity={10}
              lineSharpness={5.5}
              glow={palette.glow}
              scanDirection="vertical"
              /*
               * Near zero, against the docs' 0.7. colorSpread phase-shifts the
               * R, G and B bands apart from each other, so at the demo value the
               * sweep crest fringes into a red/green/blue prism — a pastel
               * rainbow band across the top of the login page, which is neither
               * our palette nor a look we ship. Off entirely rather than merely
               * low: the band antialiasing already keeps the crest edge from
               * looking die-cut, so the separation buys nothing here.
               */
              colorSpread={0}
              brightness={palette.brightness}
              contrast={1.15}
              softness={1.4}
              // Higher than the docs' 0.45 so the corners settle and the page
              // edges stay quiet behind the language switcher and footer.
              vignette={0.55}
              scanline
              grain
              grainIntensity={0.05}
              opacity={palette.opacity}
              mouseInteraction
              mouseRadius={0.5}
              mouseStrength={0.5}
              // The layer is pointer-events: none so the form stays clickable;
              // document-level tracking is the only way the glow follows the cursor.
              mouseSource="document"
            />
          </div>
        </Suspense>
      ) : (
        <div className="lb-static" />
      )}
      <div className="lb-scrim" />
    </div>
  );
}
